/**
 * Which build am I looking at?
 *
 * ⛔ Deliberately the OPPOSITE of every other setting in this app. `config.ts`
 * reads runtime configuration from /config.json precisely so that one image
 * serves every environment — but a version string does not describe an
 * environment, it identifies the BUNDLE. Sourcing it from the ConfigMap would
 * let a deploy assert any version it liked over an image that is actually
 * something else, which is exactly the lie the string exists to prevent. So
 * this one is baked in at build time and can only be changed by rebuilding.
 *
 * 🔑 The value is the RELEASE TAG (`$CI_COMMIT_TAG`), not package.json's
 * version. package.json has said 0.1.0 since the first commit and has never
 * once matched a deployed tag; the tag is what names the image in the registry,
 * what the Helm values pin, and what a support conversation will quote.
 */

/** Replaced at build time by Vite's `define`. See vite.config.ts. */
declare const __APP_VERSION__: string | undefined;

/**
 * A build that cannot say what it is says "dev" rather than guessing. An
 * unbuilt local run and an untagged CI build are both honestly "dev"; the one
 * outcome that must never happen is a stale number that reads as real.
 */
export const UNKNOWN_VERSION = "dev";

/**
 * The release stage, shown beside the version.
 *
 * Change this ONE constant when AnkhOS leaves beta — it is deliberately not
 * derived from the tag, because a `v1.0.0` tag is a numbering decision and
 * "we are out of beta" is a commercial one, and they will not happen on the
 * same day.
 */
export const RELEASE_STAGE = "Beta release";

/**
 * Normalise a raw build stamp for display.
 *
 * Accepts what the build systems actually produce: `v0.6.1` (a tag), `0.6.1`
 * (a hand-passed build arg), `v0.6.1-2-gabc1234` (git describe between tags).
 * The leading `v` is added when missing so the footer never renders two
 * different-looking versions for the same release.
 */
export function normaliseVersion(raw: string | undefined): string {
  const v = (raw ?? "").trim();
  if (v === "" || v === "unknown") return UNKNOWN_VERSION;
  return /^\d/.test(v) ? `v${v}` : v;
}

export const APP_VERSION: string = normaliseVersion(
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : undefined,
);

/**
 * The line as it is read: "Version: v0.6.1 · Beta release".
 *
 * 🔑 The stage travels WITH the version rather than sitting elsewhere on the
 * page. Someone quoting the version in a support mail copies the caveat with
 * it, and a screenshot of the footer carries both.
 */
export function versionLabel(version: string = APP_VERSION, stage: string = RELEASE_STAGE): string {
  return `Version: ${version} · ${stage}`;
}
